// src/utils/vaultAccessGuard.ts
// ============================================
// v6.23 内容权限前端防护
//
// 职责：
//   1. 全局 fetch 包装：所有 /vault-api 请求自动附加 X-XEAOS-Role 头
//      （服务端按角色过滤敏感路径：人生奥德赛/Config/Audit/Hermes Chats 等）
//   2. 知识中心内容保护：员工/游客不可复制粘贴（user-select 禁用 + copy 拦截）
//
// 安全原则：前端隐藏只是 UI 层，真正的过滤在服务端（server.py SENSITIVE_PREFIXES）。
// 本模块只负责把当前角色带给服务端，并做 UI 层的体验级防护。
// ============================================

const SESSION_KEY = "xeaos_session_v2";
const TOKEN_KEY = "xeaos_token_v2";

/** 读取当前登录角色（同步，从 localStorage 会话解析） */
export function getCurrentRole(): string {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return "guest";
    const user = JSON.parse(raw) as { role?: string };
    return user?.role || "guest";
  } catch {
    return "guest";
  }
}

/** 是否为管理员（super_admin） */
export function isSuperAdmin(): boolean {
  return getCurrentRole() === "super_admin";
}

/** 读取当前服务端访问令牌（登录时由 /api/login 签发并存入） */
function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

/**
 * 安装全局 fetch 包装（App 挂载时调用一次）
 * v6.x P0：所有同源 API 请求附加 X-XEAOS-Token（服务端令牌，鉴权权威依据）+
 * X-XEAOS-Role（角色头，向后兼容回落）。外部域名请求原样透传。
 */
export function installVaultRoleFetchGuard(): void {
  if (typeof window === "undefined") return;
  const win = window as unknown as { __xeaosVaultGuardInstalled?: boolean };
  if (win.__xeaosVaultGuardInstalled) return;
  win.__xeaosVaultGuardInstalled = true;

  const origFetch = window.fetch.bind(window);
  (window as any).fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    // 仅同源 API 请求附加鉴权头（外部域名请求原样透传）
    const isSameOrigin =
      typeof url === "string" &&
      (url.startsWith("/") && !url.startsWith("//")) ||
      (typeof window !== "undefined" && url.startsWith(window.location.origin));
    if (!isSameOrigin) {
      return origFetch(input, init);
    }
    const role = getCurrentRole();
    const token = getToken();
    const headers = new Headers(init?.headers || {});
    if (token && !headers.has("X-XEAOS-Token")) {
      headers.set("X-XEAOS-Token", token);
    }
    if (!headers.has("X-XEAOS-Role")) {
      headers.set("X-XEAOS-Role", role);
    }
    return origFetch(input, { ...init, headers });
  };
}

/** 员工/游客可复制？(管理员可正常复制) */
export function canCopyContent(): boolean {
  return getCurrentRole() === "super_admin";
}
